import { MatchMedia, MEDIA_TYPE } from "../../Lib/types";
import { FC, useState } from "react";
import { TextField, useForm, useModal } from "@refinedev/antd";
import { errorNotification, successNotification } from "../../../../helpers";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  AppImagePreview,
  AppImageUploader,
  AppViewCard,
  EFileTypes,
  MassImageUploader,
} from "../../../../components";
import { MatchesAlbum } from "./Matches-Album/Matches-Album";
import s from "./Matches-Media.module.scss";
import { v4 as randomId } from "uuid";
import {
  arrayLengthValidator,
  noSpecialCharValidator,
} from "../../../../validators";
import axAPI from "../../../../api/axios-instance";
import { extractMediaUrl, getVkVideoErrorMessage } from "../helpers/vk-video";

const { Title, Paragraph } = Typography;

const MATCH_MEDIA_INPUT_TYPE = {
  LINK_URL: "LINK_URL",
  LINK_IDS: "LINK_IDS",
  PHOTO: "PHOTO",
} as const;

type MatchMediaInputType =
  (typeof MATCH_MEDIA_INPUT_TYPE)[keyof typeof MATCH_MEDIA_INPUT_TYPE];

const MATCH_MEDIA_TYPE_LINK = {
  VIDEO: "video",
  PREVIEW: "preview",
  REVIEW: "review",
} as const;

type MatchMediaTypeLink =
  (typeof MATCH_MEDIA_TYPE_LINK)[keyof typeof MATCH_MEDIA_TYPE_LINK];

const matchMediaTypeLinkSelect: { key: MatchMediaTypeLink; value: string }[] = [
  { key: MATCH_MEDIA_TYPE_LINK.VIDEO, value: "Трансляция" },
  { key: MATCH_MEDIA_TYPE_LINK.PREVIEW, value: "Анонс" },
  { key: MATCH_MEDIA_TYPE_LINK.REVIEW, value: "Обзор" },
];

const mediaLinkRule = {
  validator: async (_: unknown, value?: string) => {
    const mediaUrl = extractMediaUrl(value);

    if (!mediaUrl) return;

    try {
      new URL(mediaUrl);
    } catch {
      throw new Error("Укажите корректную ссылку");
    }
  },
};

const normalizeOptionalString = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const str = String(value).trim();
  return str ? str : undefined;
};

type MatchesMediaCardProps = {
  media: MatchMedia;
  count: number;
  onRefetchCard: () => void;
  selectOptions?: { value: string; label: string }[];
  fnlId?: number | string | null;
};

type MatchMediaCardFormValues = {
  id?: number;
  type?: string;
  mediaInputType?: MatchMediaInputType;
  typeLink?: MatchMediaTypeLink;
  title?: string;
  url?: string;
  hashTags?: string[];
  photos?: { url: string }[];
  vidOid?: number | string | null;
  vidId?: number | string | null;
  videoUrl?: string | null;
};

export const MatchesMediaCard: FC<MatchesMediaCardProps> = ({
  media,
  count,
  onRefetchCard,
  selectOptions,
  fnlId,
}) => {
  const hasVkIds =
    media.vidOid !== null &&
    media.vidOid !== undefined &&
    String(media.vidOid).trim() !== "" &&
    media.vidId !== null &&
    media.vidId !== undefined &&
    String(media.vidId).trim() !== "";
  const [edit, setEdit] = useState<boolean>(false);
  const { modalProps, show, close } = useModal();

  const { formProps, form } = useForm<MatchMediaCardFormValues>({
    action: "edit",
    resource: "matchesMedia",
    meta: {
      fnlId,
    },
    dataProviderName: "matchesMediaDataProvider",
    redirect: false,
    ...successNotification({
      successData: {
        message: `Медиа ${count + 1} обновлен`,
        type: "success",
      },
      callback: () => {
        setEdit(false);
        onRefetchCard();
      },
    }),
    ...errorNotification({}),
  });

  const watchType = Form.useWatch("type", formProps.form);
  const watchInputType = Form.useWatch("mediaInputType", formProps.form);
  const isVkIdInput = watchInputType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS;

  const initialMediaInputType =
    media.type === MEDIA_TYPE.PHOTO
      ? MATCH_MEDIA_INPUT_TYPE.PHOTO
      : hasVkIds
      ? MATCH_MEDIA_INPUT_TYPE.LINK_IDS
      : MATCH_MEDIA_INPUT_TYPE.LINK_URL;

  const handleEdit = () => setEdit(true);
  const handleStopEdit = () => {
    setEdit(false);
    form.setFieldValue("url", media.url);
    form.setFieldValue("videoUrl", media.videoUrl);
    form.setFieldValue("vidOid", media.vidOid);
    form.setFieldValue("vidId", media.vidId);
    form.setFieldValue(
      "typeLink",
      media.typeLink ?? MATCH_MEDIA_TYPE_LINK.VIDEO
    );
    form.setFieldValue("mediaInputType", initialMediaInputType);
  };

  const handleFinish = async (rawValues: unknown) => {
    const values = rawValues as MatchMediaCardFormValues;

    try {
      const { vidOid, vidId, mediaInputType, url, ...rest } = values;
      const isLinkMedia =
        values.type === MEDIA_TYPE.LINK ||
        values.type === MEDIA_TYPE.ANNOUNCE_CONF ||
        values.type === MEDIA_TYPE.ANNOUNCE_REVIEW;
      const directVideoUrl =
        isLinkMedia && mediaInputType === MATCH_MEDIA_INPUT_TYPE.LINK_URL
          ? extractMediaUrl(values.videoUrl)
          : undefined;

      return await formProps.onFinish?.({
        ...rest,
        typeLink: isLinkMedia
          ? mediaInputType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS
            ? MATCH_MEDIA_TYPE_LINK.REVIEW
            : normalizeOptionalString(values.typeLink) ??
              MATCH_MEDIA_TYPE_LINK.VIDEO
          : undefined,
        url:
          values.type === MEDIA_TYPE.ANNOUNCE_CONF
            ? undefined
            : isLinkMedia
            ? normalizeOptionalString(url)
            : url,
        photos: values.type === MEDIA_TYPE.PHOTO ? values.photos : [],
        ...(isLinkMedia && mediaInputType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS
          ? {
              vidOid:
                vidOid !== null && vidOid !== undefined
                  ? String(Math.abs(Number(vidOid)))
                  : undefined,
              vidId:
                vidId !== null && vidId !== undefined
                  ? String(Number(vidId))
                  : undefined,
              videoUrl: null,
            }
          : {}),
        ...(directVideoUrl
          ? { videoUrl: directVideoUrl, vidOid: null, vidId: null }
          : {}),
      });
    } catch (error) {
      message.error(getVkVideoErrorMessage(error, "Не удалось обновить медиа"));
      return Promise.reject(error);
    }
  };

  const deleteMedia = async () => {
    try {
      await axAPI.post("/cms/match/removeMedia", null, {
        params: { mediaId: media.id },
      });

      message.success(`«Медиа ${count + 1}» удалена`);
      onRefetchCard();
      close();
    } catch (error) {
      message.error(getVkVideoErrorMessage(error, "Не удалось удалить медиа"));
    }
  };

  const MAX_ALBUM_PHOTO_COUNT = 50;
  const watchLength = Form.useWatch("photos", formProps.form);
  const stopAdding = (watchLength?.length ?? 0) < MAX_ALBUM_PHOTO_COUNT;

  return (
    <>
      <Col span={8}>
        <Card size={"small"}>
          <Space
            direction={"horizontal"}
            size={16}
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Space
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title level={5}>Медиа {count + 1}</Title>
              <DeleteOutlined
                onClick={show}
                style={{
                  color: "var(--danger)",
                  fontSize: 20,
                  cursor: "pointer",
                }}
              />
            </Space>
            {edit ? (
              <Space onClick={handleStopEdit} style={{ cursor: "pointer" }}>
                <CloseOutlined />
                Закрыть
              </Space>
            ) : (
              <EditOutlined
                style={{
                  fontSize: 20,
                  cursor: "pointer",
                  color: "var(--accent-main)",
                }}
                onClick={handleEdit}
              />
            )}
          </Space>
          {!edit ? (
            <Space
              direction={"vertical"}
              size={16}
              style={{
                width: "100%",
              }}
            >
              {media.type === MEDIA_TYPE.PHOTO && (
                <>
                  <AppViewCard label={"Обложка альбома"}>
                    {media.url ? (
                      <AppImagePreview imageURL={media.url} useS3={true} />
                    ) : (
                      <TextField value={"–"} />
                    )}
                  </AppViewCard>
                  <MatchesAlbum photos={media.photos ?? []} />
                </>
              )}
              {(media.type === MEDIA_TYPE.LINK ||
                media.type === MEDIA_TYPE.ANNOUNCE_CONF ||
                media.type === MEDIA_TYPE.ANNOUNCE_REVIEW) && (
                <>
                  {media.typeLink && (
                    <AppViewCard label={"Тип"}>
                      <TextField value={media.typeLink} />
                    </AppViewCard>
                  )}
                  {media.type !== MEDIA_TYPE.ANNOUNCE_CONF && (
                    <AppViewCard label={"Превью"}>
                      {media.url ? (
                        <AppImagePreview imageURL={media.url} useS3={true} />
                      ) : (
                        <TextField value={"–"} />
                      )}
                    </AppViewCard>
                  )}
                  {media.vidOid !== null &&
                    media.vidOid !== undefined &&
                    String(media.vidOid).trim() !== "" && (
                      <AppViewCard label={"OID"}>
                        <TextField value={media.vidOid} />
                      </AppViewCard>
                    )}
                  {media.vidId !== null &&
                    media.vidId !== undefined &&
                    String(media.vidId).trim() !== "" && (
                      <AppViewCard label={"ID"}>
                        <TextField value={media.vidId} />
                      </AppViewCard>
                    )}
                  {media.videoUrl && (
                    <AppViewCard label={"Ссылка"}>
                      <TextField value={media.videoUrl} />
                    </AppViewCard>
                  )}
                </>
              )}
              <AppViewCard label={"Заголовок:"}>
                <TextField value={media.title ?? "–"} />
              </AppViewCard>
              <AppViewCard label={"Хэштеги:"}>
                <Space direction={"horizontal"} wrap size={2}>
                  {media.hashTags.map((tag, index) => {
                    return (
                      <Tag bordered key={index} style={{ marginBottom: 8 }}>
                        {tag}
                      </Tag>
                    );
                  })}
                </Space>
              </AppViewCard>
            </Space>
          ) : (
            <Form
              style={{
                width: "100%",
              }}
              {...formProps}
              onFinish={handleFinish}
              initialValues={{
                ...media,
                typeLink: media.typeLink ?? MATCH_MEDIA_TYPE_LINK.VIDEO,
                vidOid: media.vidOid,
                vidId: media.vidId,
                mediaInputType: initialMediaInputType,
              }}
              layout={"vertical"}
            >
              <Form.Item name={"id"} hidden />
              <Form.Item name={"type"} hidden>
                <Input />
              </Form.Item>
              {watchType === "PHOTO" && (
                <>
                  <Form.Item
                    label={"Обложка альбома"}
                    name={"url"}
                    rules={[
                      {
                        required: true,
                        message: "Укажите изображение",
                      },
                    ]}
                  >
                    <AppImageUploader
                      type="MATCH"
                      entityId={`${media.matchId}/${randomId()}`}
                      serviceType={EFileTypes.MATCHES}
                      cropperProps={{
                        modalTitle: "Редактировать изображение",
                      }}
                      uploadProps={{
                        multiple: false,
                        children: "Прикрепите фото",
                        listType: "picture-card",
                      }}
                    />
                  </Form.Item>
                  <Space
                    size={8}
                    direction="vertical"
                    style={{ width: "100%" }}
                  >
                    <Paragraph className={s.newsBlockUpperHeader}>
                      <span className={s.requiredStar}>*</span> Фото альбома
                    </Paragraph>

                    <Form.List
                      name="photos"
                      rules={[
                        {
                          validator: async (_, names) => {
                            if (!names || names.length < 1) {
                              message.error(
                                "Должен быть хотя бы одно изображение"
                              );
                              return Promise.reject(
                                new Error(
                                  "Должен быть хотя бы одно изображение"
                                )
                              );
                            }
                          },
                        },
                      ]}
                    >
                      {(fields, { add, remove }) => (
                        <>
                          <div className={s.imageContainer}>
                            {fields.map((field) => (
                              <div
                                key={field.key}
                                className={s.appImageWrapper}
                              >
                                <div
                                  className={s.appImageDelete}
                                  onClick={(event) => {
                                    remove(field.name);
                                    event.stopPropagation();
                                  }}
                                >
                                  <CloseOutlined
                                    style={{
                                      color: "var(--danger)",
                                    }}
                                  />
                                </div>
                                <Form.Item
                                  name={[field.name, "url"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: (
                                        <span style={{ fontSize: "10px" }}>
                                          Укажите изображение
                                        </span>
                                      ),
                                    },
                                  ]}
                                >
                                  <AppImageUploader
                                    type="MATCH"
                                    entityId={`${media.matchId}/${randomId()}`}
                                    serviceType={EFileTypes.MATCHES}
                                    cropperProps={{
                                      modalTitle: "Редактировать изображение",
                                    }}
                                    uploadProps={{
                                      multiple: false,
                                      children: "Прикрепите фото",
                                      listType: "picture-card",
                                    }}
                                  />
                                </Form.Item>
                              </div>
                            ))}
                            {stopAdding && (
                              <div
                                className={s.addImageContainer}
                                onClick={() => add()}
                              >
                                <PlusOutlined />
                                Добавить фото
                              </div>
                            )}
                          </div>
                          {stopAdding && (
                            <MassImageUploader
                              serviceType={EFileTypes.MATCHES}
                              type="MATCH"
                              entityId={`${media.matchId}`}
                              onUpload={(url) => add({ url })}
                              canUpload={() =>
                                (form.getFieldValue("photos")?.length ?? 0) <
                                MAX_ALBUM_PHOTO_COUNT
                              }
                            />
                          )}
                        </>
                      )}
                    </Form.List>
                  </Space>
                </>
              )}

              {(watchType === "LINK" ||
                watchType === "ANNOUNCE_CONF" ||
                watchType === "ANNOUNCE_REVIEW") && (
                <>
                  <Form.Item name={"mediaInputType"} hidden>
                    <Input />
                  </Form.Item>
                  {!isVkIdInput && (
                    <Form.Item
                      name={"typeLink"}
                      label={"Тип"}
                      rules={[
                        {
                          required: true,
                          message: "Необходимо выбрать тип",
                        },
                      ]}
                    >
                      <Select placeholder="Выберите тип">
                        {matchMediaTypeLinkSelect.map((option) => (
                          <Select.Option key={option.key} value={option.key}>
                            <TextField value={option.value} />
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                  {watchType !== "ANNOUNCE_CONF" && (
                    <Form.Item
                      label={"Превью"}
                      name={"url"}
                      rules={[
                        {
                          required: true,
                          message: "Необходимо прикрепить превью",
                        },
                      ]}
                    >
                      <AppImageUploader
                        type="MATCH"
                        entityId={`${media.matchId}/${randomId()}`}
                        serviceType={EFileTypes.MATCHES}
                        cropperProps={{
                          modalTitle: "Редактировать изображение",
                        }}
                        uploadProps={{
                          multiple: false,
                          children: "Прикрепите фото",
                          listType: "picture-card",
                        }}
                      />
                    </Form.Item>
                  )}
                  {watchInputType === MATCH_MEDIA_INPUT_TYPE.LINK_URL && (
                    <Form.Item
                      name={"videoUrl"}
                      label={"Ссылка медиа"}
                      rules={[
                        {
                          required: true,
                          message: "Необходимо указать ссылку",
                        },
                        mediaLinkRule,
                      ]}
                    >
                      <Input placeholder={"Укажите ссылку"}></Input>
                    </Form.Item>
                  )}
                  {watchInputType === MATCH_MEDIA_INPUT_TYPE.LINK_IDS && (
                    <>
                      <Form.Item
                        name={"vidOid"}
                        label={"OID"}
                        rules={[
                          {
                            required: true,
                            message: "Укажите OID",
                          },
                        ]}
                      >
                        <InputNumber
                          precision={0}
                          controls={false}
                          placeholder={"Например 29484355"}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                      <Form.Item
                        name={"vidId"}
                        label={"ID"}
                        rules={[
                          {
                            required: true,
                            message: "Укажите ID",
                          },
                        ]}
                      >
                        <InputNumber
                          min={1}
                          precision={0}
                          controls={false}
                          placeholder={"Например 456247241"}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </>
                  )}
                </>
              )}
              <Form.Item
                name={"title"}
                label={"Заголовок"}
                rules={[
                  {
                    required: true,
                    message: "Необходимо указать заголовок",
                  },
                  {
                    max: 100,
                    min: 5,
                    message: "Не более 100 символов и не менее 5 символов",
                  },
                  noSpecialCharValidator,
                ]}
              >
                <Input placeholder={"Укажите заголовок"}></Input>
              </Form.Item>
              <Form.Item
                name={"hashTags"}
                label={"Хэштеги"}
                rules={[
                  arrayLengthValidator(3, "Хэштегов не может быть больше 3"),
                ]}
              >
                <Select
                  placeholder={"Укажите теги"}
                  showSearch
                  mode={"multiple"}
                  options={selectOptions}
                  notFoundContent={
                    <div
                      style={{
                        width: "100%",
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Укажите хэштеги
                    </div>
                  }
                />
              </Form.Item>

              <Button type={"primary"} htmlType={"submit"}>
                Изменить медиа
              </Button>
            </Form>
          )}
        </Card>
      </Col>
      <Modal
        okText={"Удалить медиа"}
        cancelText={"Отмена"}
        onOk={deleteMedia}
        title={"Подтверждение"}
        onCancel={close}
        {...modalProps}
      >
        Вы уверены что хотите удалить Медиа {count + 1}?
      </Modal>
    </>
  );
};
